// SCR-008 view model (REQ-034): rate-plan list rows, the editor's form state and its validation,
// plan lifecycle actions (Activate / Retire / Clone) and the "What a customer pays" illustration.
// The screen speaks % and dollars; the API speaks basis points and µUSD — the conversion happens
// here, at the edge (`parsePercentToBp`, `parseUsd`). `markupBasis` (`list` default, or `actual`)
// is a first-class field of the plan form.
import type { ApiClient } from "@nexus/ui";
import type { ApiError } from "@nexus/ui";
import type { components } from "../../api/schema";
import { type FieldErrors, hasErrors, lengthBetween, required, usdAmount } from "@nexus/ui";
import {
  bpToPercentInput,
  formatPercentBp,
  marginBpFromMarkupBp,
  markupBpFrom,
  money,
  parsePercentToBp,
  parseUsd,
  ratioBasisPoints,
  type MoneyDisplay,
} from "@nexus/ui";

export type RatePlan = components["schemas"]["RatePlan"];
export type RatePlanRate = components["schemas"]["RatePlanRate"];
export type RatePlanTier = components["schemas"]["RatePlanTier"];
export type RatePlanStatus = components["schemas"]["RatePlanStatus"];
export type MarkupBasis = components["schemas"]["MarkupBasis"];
export type TokenClassSelector = components["schemas"]["TokenClassSelector"];
export type RatePlanInput = components["schemas"]["RatePlanInput"];
export type PatchRatePlanRequest = components["schemas"]["PatchRatePlanRequest"];

export const TOKEN_CLASS_OPTIONS: readonly TokenClassSelector[] = ["*", "input", "output", "cache_read", "cache_write_5m", "cache_write_1h"];
export const MARKUP_BASIS_OPTIONS: readonly { value: MarkupBasis; label: string }[] = [
  { value: "list", label: "AWS list price" },
  { value: "actual", label: "Actual AWS bill" },
];

export function markupBasisLabel(basis: MarkupBasis): string {
  return basis === "actual" ? "Actual AWS bill" : "AWS list price";
}

const TOKEN_CLASS_LABELS: Record<TokenClassSelector, string> = {
  "*": "All token classes",
  input: "Input",
  output: "Output",
  cache_read: "Cache read",
  cache_write_5m: "Cache write (5 min)",
  cache_write_1h: "Cache write (1 hour)",
};

export function tokenClassLabel(tokenClass: TokenClassSelector): string {
  return TOKEN_CLASS_LABELS[tokenClass];
}

export type StatusTone = "blue" | "green" | "grey";

/** Draft is blue (editable), Active green, Retired grey — the redesign's chip rules. */
export function planStatusView(status: RatePlanStatus): { label: string; tone: StatusTone } {
  if (status === "active") return { label: "Active", tone: "green" };
  if (status === "retired") return { label: "Retired", tone: "grey" };
  return { label: "Draft", tone: "blue" };
}

/** A plan's markup as the screen shows it: `30%`, `12.5%` (exact, from basis points). */
export function percentLabel(bp: number): string {
  return `${bpToPercentInput(bp)}%`;
}

/** The margin a markup gives, beside it (never alone): 3000 bp → `23.1% margin`. */
export function marginLabel(markupBp: number): string {
  return `${formatPercentBp(marginBpFromMarkupBp(markupBp))} margin`;
}

// ---------------------------------------------------------------- plain (unpunctuated) amounts

/** µUSD as a plain decimal string for an editable text field (no `$`, no grouping). */
export function plainUsd(microValue: number): string {
  const negative = microValue < 0;
  const magnitude = BigInt(Math.abs(Math.trunc(microValue)));
  const units = magnitude / 1_000_000n;
  const fraction = (magnitude % 1_000_000n).toString().padStart(6, "0").replace(/0+$/, "");
  const text = fraction ? `${units}.${fraction}` : `${units}`;
  return negative ? `-${text}` : text;
}

// --------------------------------------------------------------------------------- list (SCR-008)

export type RatePlanListState =
  | { kind: "loading" }
  | { kind: "error"; error: ApiError }
  | { kind: "loaded"; plans: readonly RatePlan[]; nextCursor: string | null };

export interface RatePlanRow {
  id: string;
  name: string;
  description: string | null;
  status: RatePlanStatus;
  statusLabel: string;
  statusTone: StatusTone;
  markupBasis: MarkupBasis;
  basisLabel: string;
  markupBp: number;
  markupLabel: string;
  marginLabel: string;
  feeMicro: number;
  minimumMicro: number;
  feeDisplay: MoneyDisplay;
  minimumDisplay: MoneyDisplay;
  exceptionCount: number;
  tierCount: number;
  editable: boolean;
}

export function ratePlanRow(plan: RatePlan): RatePlanRow {
  const status = planStatusView(plan.status);
  return {
    id: plan.id,
    name: plan.name,
    description: plan.description,
    status: plan.status,
    statusLabel: status.label,
    statusTone: status.tone,
    markupBasis: plan.markupBasis,
    basisLabel: markupBasisLabel(plan.markupBasis),
    markupBp: plan.markupBp,
    markupLabel: percentLabel(plan.markupBp),
    marginLabel: marginLabel(plan.markupBp),
    feeMicro: plan.platformFeeMicroUsd,
    minimumMicro: plan.monthlyMinimumMicroUsd,
    feeDisplay: money(plan.platformFeeMicroUsd),
    minimumDisplay: money(plan.monthlyMinimumMicroUsd),
    exceptionCount: plan.rates.length,
    tierCount: plan.tiers.length,
    editable: plan.status === "draft",
  };
}

export function compareRatePlanName(a: RatePlanRow, b: RatePlanRow): number {
  return a.name.localeCompare(b.name);
}

export function compareRatePlanMarkup(a: RatePlanRow, b: RatePlanRow): number {
  return a.markupBp - b.markupBp;
}

const STATUS_ORDER: Record<RatePlanStatus, number> = { active: 0, draft: 1, retired: 2 };

/** Active first, then drafts, then retired plans. */
export function compareRatePlanStatus(a: RatePlanRow, b: RatePlanRow): number {
  return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
}

export async function loadRatePlans(client: ApiClient, cursor: string | null = null): Promise<RatePlanListState> {
  try {
    const page = await client.get("/v1/admin/rate-plans", cursor ? { query: { cursor } } : {});
    return { kind: "loaded", plans: page.items, nextCursor: page.nextCursor };
  } catch (error) {
    return { kind: "error", error: error as ApiError };
  }
}

// ------------------------------------------------------------------------------- editor (SCR-008)

export interface RateRowValues {
  model: string;
  tokenClass: TokenClassSelector;
  rateType: "markup" | "fixed";
  /** Markup as a percentage ("20", "12.5"). */
  markupPercent: string;
  fixedPerMUsd: string;
}

export interface TierRowValues {
  fromUsd: string;
  /** Discount as a percentage ("10"). */
  discountPercent: string;
}

export interface PlanFormValues {
  name: string;
  description: string;
  markupBasis: MarkupBasis;
  /** Plan-wide markup as a percentage ("30"). */
  markupPercent: string;
  platformFeeUsd: string;
  monthlyMinimumUsd: string;
  rates: readonly RateRowValues[];
  tiers: readonly TierRowValues[];
}

export function emptyPlanForm(): PlanFormValues {
  return {
    name: "",
    description: "",
    markupBasis: "list",
    markupPercent: "0",
    platformFeeUsd: "0",
    monthlyMinimumUsd: "0",
    rates: [],
    tiers: [],
  };
}

export function planFormFromPlan(plan: RatePlan): PlanFormValues {
  return {
    name: plan.name,
    description: plan.description ?? "",
    markupBasis: plan.markupBasis,
    markupPercent: bpToPercentInput(plan.markupBp),
    platformFeeUsd: plainUsd(plan.platformFeeMicroUsd),
    monthlyMinimumUsd: plainUsd(plan.monthlyMinimumMicroUsd),
    rates: plan.rates.map((rate) => rateRowFromRate(rate)),
    tiers: plan.tiers.map((tier) => ({ fromUsd: plainUsd(tier.fromMicroUsd), discountPercent: bpToPercentInput(tier.discountBp) })),
  };
}

function rateRowFromRate(rate: RatePlanRate): RateRowValues {
  const isFixed = rate.fixedPerM !== null && rate.fixedPerM !== undefined;
  return {
    model: rate.model,
    tokenClass: rate.tokenClass,
    rateType: isFixed ? "fixed" : "markup",
    markupPercent: rate.markupBp === null || rate.markupBp === undefined ? "0" : bpToPercentInput(rate.markupBp),
    fixedPerMUsd: isFixed ? plainUsd(rate.fixedPerM as number) : "0",
  };
}

/** Clone a plan into a fresh draft's form: every pricing field carries over, identity does not. */
export function cloneToForm(plan: RatePlan): PlanFormValues {
  return { ...planFormFromPlan(plan), name: `${plan.name} (copy)` };
}

/** Whether the form differs from the saved plan (Activate acts on the saved plan, not the form). */
export function formIsDirty(plan: RatePlan, values: PlanFormValues): boolean {
  return JSON.stringify(planFormFromPlan(plan)) !== JSON.stringify(values);
}

export function emptyRateRow(): RateRowValues {
  return { model: "", tokenClass: "*", rateType: "markup", markupPercent: "0", fixedPerMUsd: "0" };
}

export function emptyTierRow(): TierRowValues {
  return { fromUsd: "0", discountPercent: "0" };
}

export function addRateRow(rows: readonly RateRowValues[]): RateRowValues[] {
  return [...rows, emptyRateRow()];
}

export function removeRateRow(rows: readonly RateRowValues[], index: number): RateRowValues[] {
  return rows.filter((_, i) => i !== index);
}

export function updateRateRow(rows: readonly RateRowValues[], index: number, patch: Partial<RateRowValues>): RateRowValues[] {
  return rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
}

export function addTierRow(rows: readonly TierRowValues[]): TierRowValues[] {
  return [...rows, emptyTierRow()];
}

export function removeTierRow(rows: readonly TierRowValues[], index: number): TierRowValues[] {
  return rows.filter((_, i) => i !== index);
}

export function updateTierRow(rows: readonly TierRowValues[], index: number, patch: Partial<TierRowValues>): TierRowValues[] {
  return rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
}

/** Non-negative dollar amount (fee, minimum, fixed rate): `usdAmount` accepts negatives too. */
function nonNegativeUsd(value: string): string | null {
  const shapeError = usdAmount(value);
  if (shapeError) return shapeError;
  // usdAmount already proved this parses; the cast avoids an unreachable null branch.
  const micro = parseUsd(value) as bigint;
  return micro < 0n ? "Must not be negative." : null;
}

/** A percentage the API holds as integer basis points between `minBp` and `maxBp`. */
export function percentBetween(minBp: number, maxBp: number): (value: string) => string | null {
  return (value) => {
    const bp = parsePercentToBp(value);
    if (bp === null) return "A percentage with at most two decimals.";
    return bp < minBp || bp > maxBp ? `From ${bpToPercentInput(minBp)} % to ${Number(bpToPercentInput(maxBp)).toLocaleString("en-US")} %.` : null;
  };
}

/** Markups are 0–100,000 bp (0–1,000 %); tier discounts 0–10,000 bp (0–100 %). */
const markupPercent = percentBetween(0, 100_000);
const discountPercent = percentBetween(0, 10_000);

export function validatePlanForm(values: PlanFormValues): FieldErrors {
  const errors: FieldErrors = {};
  const nameError = required(values.name) ?? lengthBetween(1, 100)(values.name);
  if (nameError) errors.name = nameError;
  if (values.description.length > 500) errors.description = "At most 500 characters.";
  const markupError = markupPercent(values.markupPercent);
  if (markupError) errors.markupBp = markupError;
  const feeError = nonNegativeUsd(values.platformFeeUsd);
  if (feeError) errors.platformFeeUsd = feeError;
  const minimumError = nonNegativeUsd(values.monthlyMinimumUsd);
  if (minimumError) errors.monthlyMinimumUsd = minimumError;

  values.rates.forEach((rate, i) => {
    if (rate.model.trim() === "") errors[`rates.${i}.model`] = "Required: a model id, or * for every model.";
    if (rate.rateType === "markup") {
      const err = markupPercent(rate.markupPercent);
      if (err) errors[`rates.${i}.markupBp`] = err;
    } else {
      const err = nonNegativeUsd(rate.fixedPerMUsd);
      if (err) errors[`rates.${i}.fixedPerM`] = err;
    }
  });

  let previousFrom = -1n;
  values.tiers.forEach((tier, i) => {
    const fromError = nonNegativeUsd(tier.fromUsd);
    if (fromError) {
      errors[`tiers.${i}.fromMicroUsd`] = fromError;
    } else {
      const from = parseUsd(tier.fromUsd) as bigint;
      if (i === 0 && from !== 0n) errors[`tiers.${i}.fromMicroUsd`] = "The first tier starts at $0.";
      else if (from <= previousFrom) errors[`tiers.${i}.fromMicroUsd`] = "Each tier starts above the one before.";
      previousFrom = from;
    }
    const discountError = discountPercent(tier.discountPercent);
    if (discountError) errors[`tiers.${i}.discountBp`] = discountError;
  });

  return errors;
}

export function planFormIsValid(values: PlanFormValues): boolean {
  return !hasErrors(validatePlanForm(values));
}

function rateFromRow(row: RateRowValues): RatePlanRate {
  return {
    model: row.model.trim(),
    tokenClass: row.tokenClass,
    markupBp: row.rateType === "markup" ? (parsePercentToBp(row.markupPercent) ?? 0) : null,
    fixedPerM: row.rateType === "fixed" ? Number(parseUsd(row.fixedPerMUsd) ?? 0n) : null,
  };
}

function tierFromRow(row: TierRowValues): RatePlanTier {
  return { fromMicroUsd: Number(parseUsd(row.fromUsd) ?? 0n), discountBp: parsePercentToBp(row.discountPercent) ?? 0 };
}

/** Build the create/update body once the form is valid — the caller checks `planFormIsValid`. */
export function buildRatePlanInput(values: PlanFormValues): RatePlanInput {
  return {
    name: values.name.trim(),
    description: values.description.trim() === "" ? null : values.description.trim(),
    markupBasis: values.markupBasis,
    markupBp: parsePercentToBp(values.markupPercent) ?? 0,
    platformFeeMicroUsd: Number(parseUsd(values.platformFeeUsd) ?? 0n),
    monthlyMinimumMicroUsd: Number(parseUsd(values.monthlyMinimumUsd) ?? 0n),
    rates: values.rates.map(rateFromRow),
    tiers: values.tiers.map(tierFromRow),
  };
}

export function buildPatchRatePlanRequest(values: PlanFormValues): PatchRatePlanRequest {
  return buildRatePlanInput(values) as PatchRatePlanRequest;
}

// ------------------------------------------------------------------------------- lifecycle actions

export interface PlanActionsView {
  canEdit: boolean;
  canActivate: boolean;
  canRetire: boolean;
  canClone: boolean;
}

/** Activate / Retire / Clone visibility (SCR-008): gated on `billing:write` and the plan's status. */
export function planActions(plan: RatePlan, canWrite: boolean): PlanActionsView {
  return {
    canEdit: canWrite && plan.status === "draft",
    canActivate: canWrite && plan.status === "draft",
    canRetire: canWrite && plan.status === "active",
    canClone: canWrite && plan.status !== "draft",
  };
}

export type PlanDetailState =
  | { kind: "loading" }
  | { kind: "error"; error: ApiError }
  | { kind: "loaded"; plan: RatePlan };

/** `PATCH .../rate-plans/{planId}` answers 409 `conflict` when another request saved first
 *  (`ports.ErrPlanModified`): the editor's job is to say so plainly and reload the plan it
 *  overtook, not to keep offering a Save that will only fail again. */
export function isPlanConflict(error: Pick<ApiError, "code">): boolean {
  return error.code === "conflict";
}

export const PLAN_CONFLICT_MESSAGE = "This rate plan was changed by someone else. Reloading the latest version — your unsaved changes here were not applied.";

export async function loadRatePlan(client: ApiClient, planId: string): Promise<PlanDetailState> {
  try {
    const plan = await client.get("/v1/admin/rate-plans/{planId}", { path: { planId } });
    return { kind: "loaded", plan };
  } catch (error) {
    return { kind: "error", error: error as ApiError };
  }
}

// ------------------------------------------------------------------ "What a customer pays" (SCR-008)

/**
 * An illustration of the plan's own settings on one amount of AWS usage, in the order the pricing
 * engine applies them (docs/guides/revenue-billing.md, "Arithmetic order"): plan-wide markup
 * (rounded half-up), graduated volume discount (rounded once), the minimum true-up compared with
 * the discounted usage, then the platform fee. It is exact BigInt µUSD arithmetic on a
 * hypothetical amount — not a preview of any customer's invoice: model exceptions, trials and
 * adjustments are left out, and real invoices are priced by the server per model, class and day.
 */
export interface WorkedExample {
  kind: "ready";
  costMicro: bigint;
  markupBp: number;
  markupMicro: bigint;
  discountMicro: bigint;
  topUpMicro: bigint;
  feeMicro: bigint;
  totalMicro: bigint;
  profitMicro: bigint;
  /** Profit ÷ cost, and profit ÷ invoice — shown side by side. */
  profitMarkupBp: bigint | null;
  profitMarginBp: bigint | null;
  /** Rate rows the illustration leaves out. */
  exceptionsLeftOut: number;
}

export type WorkedExampleResult = WorkedExample | { kind: "invalid-usage" } | { kind: "invalid-plan" };

export const DEFAULT_EXAMPLE_USAGE = "1000";

/** round(numerator ÷ denominator), half-up, for a non-negative numerator. */
function divideHalfUp(numerator: bigint, denominator: bigint): bigint {
  return (2n * numerator + denominator) / (2n * denominator);
}

/** The graduated discount on a usage price, rounded once. Tiers are already valid and ascending. */
export function graduatedDiscount(usagePrice: bigint, tiers: readonly { fromMicro: bigint; discountBp: number }[]): bigint {
  let weighted = 0n;
  tiers.forEach((tier, i) => {
    const next = tiers[i + 1]?.fromMicro ?? null;
    const top = next === null || next > usagePrice ? usagePrice : next;
    if (top > tier.fromMicro) weighted += (top - tier.fromMicro) * BigInt(tier.discountBp);
  });
  return divideHalfUp(weighted, 10_000n);
}

const PRICING_ERROR_KEYS = /^(markupBp|platformFeeUsd|monthlyMinimumUsd|tiers\.)/;

export function workedExample(values: PlanFormValues, usageInput: string): WorkedExampleResult {
  const cost = parseUsd(usageInput);
  if (cost === null || cost < 0n) return { kind: "invalid-usage" };
  if (Object.keys(validatePlanForm(values)).some((key) => PRICING_ERROR_KEYS.test(key))) return { kind: "invalid-plan" };

  const markupBp = parsePercentToBp(values.markupPercent) as number;
  const usagePrice = divideHalfUp(cost * BigInt(10_000 + markupBp), 10_000n);
  const tiers = values.tiers.map((tier) => ({ fromMicro: parseUsd(tier.fromUsd) as bigint, discountBp: parsePercentToBp(tier.discountPercent) as number }));
  const discount = graduatedDiscount(usagePrice, tiers);
  const discounted = usagePrice - discount;
  const minimum = parseUsd(values.monthlyMinimumUsd) as bigint;
  const topUp = minimum > discounted ? minimum - discounted : 0n;
  const fee = parseUsd(values.platformFeeUsd) as bigint;
  const total = discounted + topUp + fee;
  const profit = total - cost;
  return {
    kind: "ready",
    costMicro: cost,
    markupBp,
    markupMicro: usagePrice - cost,
    discountMicro: discount,
    topUpMicro: topUp,
    feeMicro: fee,
    totalMicro: total,
    profitMicro: profit,
    profitMarkupBp: markupBpFrom(profit, cost),
    profitMarginBp: ratioBasisPoints(profit, total),
    exceptionsLeftOut: values.rates.length,
  };
}
